import { useQuery } from '@tanstack/react-query';
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { View } from 'react-native';

import { Spinner, Text, VStack } from '@squire/ui';

import { customerSaleQueryOptions } from '@/api/customer/customerSaleQuery';
import {
  BottomSheet,
  type BottomSheetRef,
} from '@/components/_shared/BottomSheet';
import { CloseButton } from '@/components/_shared/CloseButton';
import { useToast } from '@/components/_shared/Toaster';
import { AppointmentRatingMessageStep } from '@/components/appointment/AppointmentRatingMessageStep';
import { AppointmentRatingStarsStep } from '@/components/appointment/AppointmentRatingStarsStep';
import { useSubmitAppointmentRatingMutation } from '@/hooks/mutations/appointment/useSubmitAppointmentRatingMutation';
import { useOnMount } from '@/hooks/shared/useOnMount';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { DateFormat, formatDateWithTimezone } from '@/utils/dateUtils';
import { Tracker } from '@/utils/tracking';

import { styles } from './styles';
import type {
  AppointmentRatingPromptModalProps,
  AppointmentRatingPromptStep,
} from './types';

export const AppointmentRatingPromptModal = forwardRef<
  BottomSheetRef,
  AppointmentRatingPromptModalProps
>(({ appointmentId, onClose, onSubmitted }, ref) => {
  const { t } = useScreenTranslations('AppointmentRatingPromptModal');
  const { toast } = useToast();
  const internalRef = useRef<BottomSheetRef>(null);
  // Tracks how the sheet was closed so we only dismiss server-side after a
  // rating — not on a close-button / swipe / backdrop dismissal.
  const closeIntent = useRef<'submitted' | null>(null);

  const [step, setStep] = useState<AppointmentRatingPromptStep>('stars');
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [reviewText, setReviewText] = useState('');

  useImperativeHandle(ref, () => ({
    open: (animated) => internalRef.current?.open(animated),
    close: (animated) => internalRef.current?.close(animated),
    snapTo: (index, animated) => internalRef.current?.snapTo(index, animated),
    snapToIndex: (index, animated) =>
      internalRef.current?.snapToIndex(index, animated),
  }));

  const { data: saleData, isError } = useQuery(
    customerSaleQueryOptions(appointmentId),
  );

  useOnMount(() => {
    internalRef.current?.open();
    Tracker.track('appointment_rating_prompt_shown', { appointmentId });
  });

  const submitRatingMutation = useSubmitAppointmentRatingMutation({
    onSuccess: () => {
      toast({ variant: 'success', message: t('successMessage') });
      closeIntent.current = 'submitted';
      internalRef.current?.close();
    },
  });

  const saleItem = saleData?.items[0];
  const appointment = saleItem?.appointment;
  const barber = saleItem?.barber;
  const professionalFirstName = barber?.firstName ?? '';
  const professionalPhotoUrl = barber?.photos[0]?.url;
  const formattedDateTime =
    saleData && appointment
      ? formatDateWithTimezone(
          appointment.dateTime,
          DateFormat.DATE_TIME,
          saleData.shop.timezone,
        )
      : '';

  const handleNextStep = () => setStep('message');

  const handleStarPress = (rating: number) => setSelectedRating(rating);

  // Fires after the BottomSheet close animation completes (swipe, backdrop, or
  // programmatic close).
  const handleSheetDismissed = () => {
    Tracker.track('appointment_rating_prompt_dismissed', {
      appointmentId,
      intent: closeIntent.current ?? 'dismissed',
    });

    if (closeIntent.current === 'submitted') {
      onSubmitted();
    } else {
      // Close button / swipe / backdrop: hide for this session, do not dismiss
      // the notifications server-side.
      onClose();
    }
    closeIntent.current = null;
  };

  const handleClose = () => internalRef.current?.close();

  const handleSubmit = () => {
    if (selectedRating === null || !appointment) return;

    submitRatingMutation.mutate({
      appointmentId: appointment.id,
      saleOrderIdOrAppointmentId: appointmentId,
      appointmentRating: selectedRating,
      appointmentReview: reviewText.trim() || undefined,
    });
  };

  const renderContent = () => {
    if (isError) {
      return (
        <VStack style={styles.stateContainer}>
          <Text variant="m" color="textSecondary">
            {t('loadError')}
          </Text>
        </VStack>
      );
    }

    if (!saleData || !appointment) {
      return (
        <VStack style={styles.stateContainer}>
          <Spinner />
        </VStack>
      );
    }

    if (step === 'stars') {
      return (
        <AppointmentRatingStarsStep
          professionalPhotoUrl={professionalPhotoUrl}
          professionalFirstName={professionalFirstName}
          formattedDateTime={formattedDateTime}
          selectedRating={selectedRating}
          onRatingSelect={handleStarPress}
          onNextStep={handleNextStep}
          pinActionButton={false}
        />
      );
    }

    return (
      <AppointmentRatingMessageStep
        professionalPhotoUrl={professionalPhotoUrl}
        professionalFirstName={professionalFirstName}
        reviewText={reviewText}
        onReviewTextChange={setReviewText}
        selectedRating={selectedRating}
        onSubmit={handleSubmit}
        isSubmitting={submitRatingMutation.isPending}
        pinActionButton={false}
      />
    );
  };

  return (
    <BottomSheet
      ref={internalRef}
      name="appointment-rating-prompt"
      testID="appointment-rating-prompt"
      onDismiss={handleSheetDismissed}
      dismissible
    >
      <VStack style={styles.content}>
        <View style={styles.headerContainer}>
          <CloseButton
            testID="rating-prompt-close"
            onPress={handleClose}
            accessibilityLabel={t('accessibility.closeButton')}
            accessibilityHint={t('accessibility.closeButtonHint')}
          />
        </View>
        {renderContent()}
      </VStack>
    </BottomSheet>
  );
});

AppointmentRatingPromptModal.displayName = 'AppointmentRatingPromptModal';
